/**
 * StatCard — clickable summary tile used in the board header strip.
 *
 * Clicking toggles a status filter on the board table below, so the
 * card doubles as a filter chip. When `active` is true the card shows
 * an inset ring (matching BOSS's selection pattern).
 *
 * Props
 *   label    {string}  — upper-case label above the count (e.g. "NEEDS REVIEW")
 *   value    {number}  — count to display
 *   accent   {"success"|"warn"|"danger"|"info"|"neutral"} — BOSS status token
 *   active   {boolean} — whether this filter is currently active
 *   onClick  {() => void}
 *   testId   {string}  — data-testid for QA
 */
export default function StatCard({
  label,
  value,
  accent = 'neutral',
  active = false,
  onClick,
  testId,
}) {
  const valueColorClass = {
    success: 'text-[var(--boss-success-text)]',
    warn: 'text-[var(--boss-warning-text)]',
    danger: 'text-[var(--boss-danger-text)]',
    info: 'text-[var(--boss-info-text)]',
    neutral: 'text-boss-text',
  }[accent] ?? 'text-boss-text';

  const activeBg = {
    success: 'bg-[var(--boss-success-soft)]',
    warn: 'bg-[var(--boss-warning-soft)]',
    danger: 'bg-[var(--boss-danger-soft)]',
    info: 'bg-[var(--boss-info-soft)]',
    neutral: 'bg-boss-elevated',
  }[accent] ?? 'bg-boss-elevated';

  // Only render an interactive <button> when there is something to click. A
  // <button> with onClick === undefined still tab-focuses, still highlights on
  // hover and still activates on Enter — it just does nothing, which reads as
  // broken. That matters most where clickable and non-clickable cards sit side by
  // side (Audit Trail: three cards filter, "Override Rate" does not).
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'div';

  return (
    <Tag
      {...(interactive ? { type: 'button', onClick } : {})}
      data-testid={testId}
      className={[
        'rounded-[10px] border px-3 py-2.5 text-left transition-colors min-w-0 w-full',
        active
          ? `border-boss-border ring-1 ring-inset ring-boss-border ${activeBg}`
          : interactive
            ? 'border-boss-border bg-boss-surface hover:bg-boss-surface-elevated'
            : 'border-boss-border bg-boss-surface',
      ].join(' ')}
    >
      <div className="text-[10px] font-semibold tracking-widest uppercase text-boss-text-secondary truncate">
        {label}
      </div>
      <div className={`text-[22px] font-semibold tabular-nums mt-0.5 leading-none ${valueColorClass}`}>
        {value}
      </div>
    </Tag>
  );
}
